import { useEffect, useRef } from 'react';
import type { ReactNode, RefObject } from 'react';
import { ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Shell of the desktop tool drawers (uploads, motifs, fonts, colours): a
 * card floating over the canvas with a title, the content from outside and
 * a tab on the right edge that closes it. The tab is the only close control
 * — a second X in the title row would compete with it for the focus.
 *
 * It lies absolutely over the canvas column instead of pushing it aside:
 * a drawer that took layout space would resize the canvas' parent and make
 * the CanvasEditor's ResizeObserver rescale the design on every open and
 * close (cf. docs/solutions/ui-bugs/wrong-product-mockup-edit-placement.md).
 *
 * Escape is deliberately not handled here. The design step owns the order
 * in which Escape closes things (inline text editing, dialog, drawer), and
 * a second listener in the drawer would close it out of turn.
 */
export function DirectSaleDesktopDrawer({
  title,
  subtitle,
  onClose,
  returnFocusRef,
  children,
}: {
  title: string;
  /** One line under the title, e.g. the name of the colour in use. */
  subtitle?: string;
  onClose: () => void;
  /** Receives focus back when the drawer closes. */
  returnFocusRef?: RefObject<HTMLElement>;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const tabRef = useRef<HTMLButtonElement>(null);

  // Keyboard users land on the control that closes the drawer and get back
  // to the tool they came from. Mount/unmount only: the parent swaps the
  // content (upload -> motifs) by remounting, and a title change alone must
  // not pull the focus out of the content.
  useEffect(() => {
    tabRef.current?.focus();
    return () => {
      // Only a focus the drawer took with it is handed back (the browser
      // parks it on the body then). What closed the drawer may have focused
      // something itself — "Text" puts the keyboard into the text on the
      // product — and must keep it.
      const active = document.activeElement;
      if (active !== null && active !== document.body) return;
      // Read at close time on purpose: the element to return to belongs to
      // the parent and may have been re-rendered while the drawer was open.
      // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
      returnFocusRef?.current?.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, []);

  return (
    // The outer box must not clip: the tab sticks out over the right edge.
    // Scrolling therefore happens one level further in, which also keeps
    // the content inside the rounded corners.
    <div
      role="region"
      aria-label={title}
      className="absolute bottom-3 left-3 top-3 z-20 w-[300px] rounded-section border border-line bg-paper min-[1200px]:w-[370px]"
    >
      <div className="flex h-full flex-col overflow-y-auto rounded-section">
        <div className="flex-none px-6 pb-3 pt-6">
          <h2 className="font-display text-[20px] font-bold leading-tight text-ink">
            {title}
          </h2>
          {subtitle !== undefined && (
            <p className="mt-1 text-sm text-ink-2">{subtitle}</p>
          )}
        </div>
        {children}
      </div>

      {/* Overlaps the card's border by a pixel so the two lines read as one. */}
      <button
        ref={tabRef}
        type="button"
        aria-label={t('directSale.desktop.closePanel')}
        onClick={onClose}
        className="absolute left-full top-1/2 -ml-px flex h-12 w-9 -translate-y-1/2 items-center justify-center rounded-r-control border border-l-0 border-line bg-paper text-ink-2 transition-colors hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
